import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { errorTitle, isRetryable, userMessage } from '../lib/errors';
import { Button } from './Button';

export interface ErrorStateProps {
  /**
   * The caught error. Pass it raw — this component decides the wording, and the
   * rules it applies are not obvious enough to reimplement per screen.
   */
  error?: unknown;
  /** Overrides the derived heading. */
  title?: string;
  /** Overrides the derived body. Use only when the screen genuinely knows better. */
  description?: ReactNode;
  onRetry?: () => void;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * The failure state for a screen or a panel.
 *
 * The cases that matter, and why (rules in lib/errors.ts):
 *
 * 42501 — "You don't have access to this". True and safe to say.
 *
 * P0002 / 404 — "Not found", full stop. RLS returns nothing both for records that do
 *       not exist and for records in another gym or outside the reader's scope, so
 *       the two cannot be told apart. Rendering "you may not have access" here would
 *       undo that and confirm the record exists somewhere. A test asserts this
 *       component never says so.
 *
 * P0001 — the database's own refusal, verbatim. It is written for the person, it is
 *       actionable, and paraphrasing it loses the reason.
 */
export function ErrorState({
  error,
  title,
  description,
  onRetry,
  className,
  size = 'md',
}: ErrorStateProps) {
  const derivedTitle = title ?? errorTitle(error);
  const derivedDescription = description ?? userMessage(error);

  // Retrying a refusal gets the same refusal, so the affordance is offered only for
  // failures that might go away (a dropped connection, an unknown error).
  const retryable = onRetry && isRetryable(error);

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      <svg
        className="h-8 w-8 text-ink-subtle"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4.5" strokeLinecap="round" />
        <circle cx="12" cy="16" r="0.75" fill="currentColor" stroke="none" />
      </svg>

      <h3 className={cn('font-semibold text-ink', size === 'sm' ? 'text-base' : 'text-lg')}>
        {derivedTitle}
      </h3>
      <p className="max-w-md text-balance text-sm text-ink-muted">{derivedDescription}</p>

      {retryable && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-1">
          Try again
        </Button>
      )}
    </div>
  );
}
